import { useEffect, useState } from "react";
import { ArrowUp, Check } from "lucide-react";
import { theme } from "../theme";
import type { Release } from "../hooks/useVersionCheck";

interface UpdateBannerProps {
  updateAvailable: boolean;
  justUpdated: boolean;
  releases: Release[];
  onDismissJustUpdated: () => void;
}

export function UpdateBanner({
  updateAvailable,
  justUpdated,
  releases,
  onDismissJustUpdated,
}: UpdateBannerProps) {
  const [toastVisible, setToastVisible] = useState(false);

  useEffect(() => {
    if (!justUpdated) return;
    setToastVisible(true);
    const hide = setTimeout(() => setToastVisible(false), 4000);
    const cleanup = setTimeout(onDismissJustUpdated, 4300);
    return () => { clearTimeout(hide); clearTimeout(cleanup); };
  }, [justUpdated, onDismissJustUpdated]);

  return (
    <>
      {/* "Update available" — sticky bottom bar, persists until logout */}
      {updateAvailable && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9998,
            background: theme.color.accent,
            color: "#fff",
            padding: "10px 24px",
            fontSize: 13,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            fontFamily: theme.font,
            boxShadow: "var(--c-sheetShadow)",
          }}
        >
          <ArrowUp size={14} style={{ opacity: 0.85, flexShrink: 0 }} />
          Update available — sign out and back in to apply the latest changes
        </div>
      )}

      {/* "Update complete" — toast, auto-dismisses after 4 seconds */}
      {justUpdated && (
        <div
          style={{
            position: "fixed",
            bottom: updateAvailable ? 52 : 24,
            right: 24,
            zIndex: 9999,
            background: theme.color.success,
            color: "#fff",
            borderRadius: 3,
            padding: "12px 18px",
            fontSize: 13,
            fontWeight: 600,
            boxShadow: "var(--c-sheetShadow)",
            opacity: toastVisible ? 1 : 0,
            transition: "opacity 0.3s ease",
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            maxWidth: 340,
            fontFamily: theme.font,
            pointerEvents: "none",
          }}
        >
          <Check size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <div>Update complete</div>
            {/* Only the customer-facing note (AUD-21); an entry from before the field existed shows its subject. */}
            {releases[0] && (releases[0].note === undefined ? releases[0].summary : releases[0].note) && (
              <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.9, marginTop: 3, lineHeight: 1.4 }}>
                {releases[0].note === undefined ? releases[0].summary : releases[0].note}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
